import React, { useState, useEffect } from "react";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import { Box, Button, TextField } from "@mui/material";
import Chip from "@mui/material/Chip";
import htmlToDraft from "html-to-draftjs";
import { v4 as uuidv4 } from "uuid";
import { useAuthUser } from "../store/AuthUserContextProvider";
import PrintIcon from "@mui/icons-material/Print";

import TEMPLATE_PLACEHOLDER_IMG from "../img/layout-template.png";

import {
  createTemplate,
  getTemplates,
  deleteTemplate,
  spellchecker,
} from "../api/api";

import { withSnackbar } from "notistack";
import Loader from "./common/Loader";

const DraftEditor = (props) => {
  const { authUser } = useAuthUser();
  const IS_REF_DOC = authUser.Role === "ref-doctor";
  const [templates, setTemplates] = useState([]);
  const blocksFromHtml = htmlToDraft(props.note);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(
    contentBlocks,
    entityMap
  );

  const { enqueueSnackbar, handleNoteChange } = props;

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(contentState)
  );

  const [spellCheckerResult, setSpellCheckerResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getTemplates().then((res) => {
      const [data, err] = res;
      if (err) {
        enqueueSnackbar("Error: " + err.message, {
          preventDuplicate: true,
          variant: "warning",
        });
      } else {
        setTemplates(data.data);
      }
    });
  }, []);

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
  };

  const handleCreateTemplate = async () => {
    let name = prompt("Please enter a template name");
    const markup = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    const templatesWithSameName = templates.filter(
      (template) => template.Name === name
    );

    if (name.length === 0) {
      enqueueSnackbar("Name cannot be blank", {
        preventDuplicate: true,
        variant: "warning",
      });
      return;
    } else {
      if (templatesWithSameName.length > 0) {
        enqueueSnackbar(`"${name}" already exists`, {
          preventDuplicate: true,
          variant: "warning",
        });
      } else {
        const template = {
          Name: name,
          Creator: authUser.FirstName + " " + authUser.LastName,
          CreatorId: authUser.Id,
          CreateAt: new Date(),
          Timestamp: new Date().getTime(),
          Markup: markup,
          Id: uuidv4(),
        };

        const newTemplatesList = [...templates, template];
        const res = await createTemplate(template);
        const [, err] = res;

        if (err) {
          enqueueSnackbar("Error: " + err.message, {
            preventDuplicate: true,
            variant: "warning",
          });
        } else {
          setTemplates(newTemplatesList);

          enqueueSnackbar("Template creation successful", {
            preventDuplicate: true,
            variant: "success",
          });
        }
      }
    }
  };

  const handleDeleteTemplate = async (id, name) => {
    if (window.confirm(`Delete the template named '${name}'?`)) {
      const filterOutTemplate = templates.filter((template) => {
        return template.Id !== id;
      });

      const res = await deleteTemplate(id);
      const [, err] = res;

      if (err) {
        enqueueSnackbar("Error: " + err.message, {
          preventDuplicate: true,
          variant: "error",
        });
      } else {
        setTemplates(filterOutTemplate);

        enqueueSnackbar("Successfully delete template " + name, {
          preventDuplicate: true,
          variant: "success",
        });
      }
    }
  };

  const handleChooseTemplate = (markup) => {
    const blcksFromHtml = htmlToDraft(markup);
    const { contentBlocks: blocks, entityMap: map } = blcksFromHtml;
    const state = ContentState.createFromBlockArray(blocks, map);

    setEditorState(EditorState.createWithContent(state));
  };

  const handleSpellCheck = async (text) => {
    setIsLoading(true);
    let rawText = convertToRaw(text.getCurrentContent()).blocks[0].text;

    const [data, err] = await spellchecker({ data: rawText });
    if (err) {
      setIsLoading(false);
      props.enqueueSnackbar("There was an error spell checking", {
        preventDuplicate: true,
        variant: "error",
      });
      return;
    } else {
      setIsLoading(false);
      setSpellCheckerResult(data.data);
      console.log("text", data);
    }
  };

  useEffect(() => {
    const markup = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    handleNoteChange(markup);
  }, [editorState]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Editor
        editorState={editorState}
        wrapperClassName="editor-wrapper"
        editorClassName="editor-editor"
        readOnly={IS_REF_DOC}
        toolbar={{
          options: [
            "inline",
            "blockType",
            "fontSize",
            "fontFamily",
            "list",
            "textAlign",
            "link",
          ],
        }}
        onEditorStateChange={onEditorStateChange}
      />

      <Box
        sx={{
          padding: "16px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {props.isDone && (
          <Button
            onClick={props.handleReportPrint}
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PrintIcon />}
            sx={{ mr: 2 }}
          >
            Print Report
          </Button>
        )}

        {!IS_REF_DOC && (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => handleSpellCheck(editorState)}
          >
            Spell Check
          </Button>
        )}
      </Box>
      {/* {!IS_REF_DOC && (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleCreateTemplate}
          >
            Save as Template
          </Button>
        )}
     

      {!IS_REF_DOC && (
        <TemplatesPanel
          templates={templates}
          handleDelete={handleDeleteTemplate}
          chooseTemplate={handleChooseTemplate}
        />
      )} */}

      {spellCheckerResult && (
          <TextField
            id="filled-multiline-static"
            label="Spell-Checker results"
            multiline
            defaultValue={spellCheckerResult}
            variant="filled"
            sx={{ width: "100%", height: "100%" }}
          />
        )}

      {isLoading && (
        <Loader />
      )}
    </Box>
  );
};

const TemplatesPanel = (props) => {
  return (
    <Box
      sx={{
        width: "100%",
        background: "#222",
        padding: "16px",
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "23px",
          width: 1,
          width: "100%",
          cursor: "default",
        }}
      >
        Standard Reports
      </Box>

      {props.templates.map((template, index) => {
        return (
          <TemplateCard
            key={"key-" + index}
            template={template}
            handleDelete={props.handleDelete}
            chooseTemplate={props.chooseTemplate}
          />
        );
      })}
    </Box>
  );
};

const TemplateCard = (props) => {
  return (
    <Button
      sx={{
        cursor: "pointer",
        padding: "16px",
        paddingTop: "45px",
        flexDirection: "column",
        alignItems: "flex-start",
        color: "#ddd",
        position: "relative",
      }}
      color="secondary"
      className="template-card"
      onClick={() => {
        props.chooseTemplate(props.template.Markup);
      }}
    >
      <Chip
        label="x"
        variant="outlined"
        sx={{
          position: "absolute",
          right: "12px",
          top: "5px",
          transform: "scale(.8)",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          props.handleDelete(props.template.Id, props.template.Name);
        }}
        className="delete-template-btn"
      />

      <Box
        sx={{
          width: "200px",
          height: "60px",
          background: `url(${TEMPLATE_PLACEHOLDER_IMG})`,
          backgroundSize: "contain",
          borderRadius: 1,
          mb: 2,
        }}
      />

      <Box
        sx={{
          fontWeight: "700",
        }}
      >
        {props.template.Name}
      </Box>

      <Box sx={{ fontSize: 10, opacity: 0.7 }}>
        by - {props.template.Creator}
      </Box>
    </Button>
  );
};

export default withSnackbar(DraftEditor);
